import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { DataTableManagerComponent, LoadDataEvent } from '@shared/components/data-table-manager/data-table-manager.component';
import { takeUntil } from 'rxjs/operators';
import { CommentSearchContent, FieldFilter, SearchResult } from '@shared/models/search.model';
import { CommentBaseComponent } from '@shared/models/comments/comment.model';
import { ActivatedRoute } from '@angular/router';
import { CommentService } from '@services/comment/comment.service';
import { ConfigService } from '@services/config.service';
import { Subject } from 'rxjs';
import {
  FieldsFilterDomainMarketLang,
  FilterDomainMarketLangComponent,
} from '@shared/components/filter-domain-market-lang/filter-domain-market-lang.component';
import { CommentModerationService } from '@services/comment/comment-moderation.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CommentAction, EmittedAction } from '@shared/constants/comment.constant';
import { CommentDataMapper } from '@services/comment/comment-data-mapper';
import { SelectionModel } from '@angular/cdk/collections';
import { MatButtonModule } from '@angular/material/button';

@Component({
  selector: 'app-comment-moderation',
  templateUrl: './comment-moderation.component.html',
  styleUrls: ['./comment-moderation.component.scss'],
  imports: [DataTableManagerComponent, FilterDomainMarketLangComponent, TranslateModule, MatButtonModule],
})
export class CommentModerationComponent extends CommentBaseComponent implements OnInit, OnDestroy {
  selection = new SelectionModel<CommentSearchContent>(true, []);
  isButtonDisabled = true;
  elementStyles: { [commentId: string]: string } = {};
  forceReloadData: Subject<void> = new Subject<void>();
  fieldsFilterDomainMarketLang: FieldsFilterDomainMarketLang = {
    domainFieldFilter: 'recipe.domain.key',
    marketFieldFilter: 'recipe.market.key',
    langFieldFilter: 'recipe.lang.key',
  };
  protected readonly CommentAction = CommentAction;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly commentService: CommentService,
    private readonly commentMapper: CommentDataMapper,
    private readonly configService: ConfigService,
    private readonly commentModerationService: CommentModerationService,
    private readonly translateService: TranslateService,
    private readonly cdRef: ChangeDetectorRef,
  ) {
    super();
    this.DEFAULT_FILTER_CONFIG.isFieldFilterEnabled = true;
    this.displayInfo.noDataMessage = 'COMMENTS.SEARCH.NO_RESULT';
    this.defaultSortActive = 'creationDate';
    this.defaultSortDirection = 'desc';
  }

  /**
   * Returns true if all selected comments are in the 'PUBLISHED' status.
   */
  get isSelectionPublished(): boolean {
    return this.selection.selected.every((item) => (item as any).status === this.translateService.instant('PUBLISHED'));
  }

  ngOnInit(): void {
    this.commentDomainMarketLang = this.commentService.initDomainMarketLang(this.configService);
    this.createColumnConfig();
    const resolvedData = this.route.snapshot.data.commentData;
    this.domains = resolvedData?.domains;
    this.markets = resolvedData?.markets;
    this.commentReasons = resolvedData?.commentReasons;
    this.commentStatuses = resolvedData?.commentStatuses;
    this.domainMarketLang.domains = resolvedData?.domains;
    this.domainMarketLang.markets = resolvedData?.markets;
    this.domainMarketLang.languages = resolvedData?.languages;

    this.commentModerationService.elementStyles$.pipe(takeUntil(this.destroy$)).subscribe((styles) => {
      this.elementStyles = { ...this.elementStyles, ...styles };
      this.cdRef.detectChanges();
    });

    this.commentModerationService.reloadComments$.pipe(takeUntil(this.destroy$)).subscribe((loadDataEvent) => {
      this.forceReloadData.next();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadComments(event: LoadDataEvent): void {
    this.commentService
      .getCommentList(
        event.filterParams.paginator?.pageIndex ?? this.pageIndex,
        event.filterParams.paginator?.pageSize ?? this.pageSize,
        this.commentDomainMarketLang,
        event.filterParams.query,
        event.filterParams.sort,
        event.facetFilters,
        event.fieldFilters,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe((searchResult: SearchResult<CommentSearchContent>) => {
        const { tableData, displayInfo, facets } = this.commentMapper.mapCommentData(
          searchResult,
          this.domains,
          this.markets,
          this.commentStatuses,
          this.commentReasons,
          this.commentDomainMarketLang,
          this.domainMarketLang,
          this.translateService,
        );
        this.tableData.data = tableData ?? [];

        this.displayInfo = displayInfo;
        this.facetGroups.next([{ title: 'FACETS.STANDARD', facets }]);
        this.onSelectionChanged(this.selection);
      });
  }

  handleAction(emittedAction: EmittedAction<CommentSearchContent>): void {
    const { action } = emittedAction;

    switch (action) {
    case CommentAction.REJECT:
      this.commentModerationService
        .rejectComments(emittedAction.selection, this.commentDomainMarketLang, this.commentStatuses, this.commentReasons)
        .subscribe();
      break;

    case CommentAction.DELETE:
      this.commentModerationService.deleteComments(emittedAction.selection).pipe(takeUntil(this.destroy$)).subscribe();
      break;

    case CommentAction.APPROVE:
      this.commentModerationService.approveComments(emittedAction.selection);
      break;

    default:
      console.error('Invalid action');
      return;
    }
  }

  handleGlobalFilterChange(fieldFilter: FieldFilter[]): void {
    this.fieldFilters = fieldFilter.slice();
  }

  handleCommentAction(selection: SelectionModel<CommentSearchContent>, action: CommentAction): void {
    const emittedAction: EmittedAction<CommentSearchContent> = {
      action,
      selection,
    };

    switch (action) {
    case CommentAction.REJECT:
      emittedAction.commentDomainMarketLang = this.domainMarketLang;
      this.handleAction(emittedAction);
      break;

    case CommentAction.DELETE:
      this.handleAction(emittedAction);
      break;

    case CommentAction.APPROVE:
      this.handleAction(emittedAction);
      break;

    default:
      return;
    }
  }

  handleReloadData(event: LoadDataEvent): void {
    this.loadComments(event);
  }

  onSelectionChanged(newSelection: SelectionModel<CommentSearchContent>): void {
    this.selection.clear();
    for (const item of newSelection.selected) {
      this.selection.select(item);
    }
    this.isButtonDisabled = !this.selection.hasValue() || this.isSelectionPublished;
  }

  private createColumnConfig(): void {
    this.columnConfigs = [
      {
        dataFieldName: '',
        name: 'select',
        headerTranslationKey: '',
        content: {
          type: 'action',
          actions: [
            {
              label: 'Select',
              type: 'select',
              icon: 'select',
            },
          ],
        },
        sortable: false,
      },
      {
        dataFieldName: 'domain',
        name: 'recipe.domain',
        headerTranslationKey: 'COMMENTS.SEARCH.COMMENT_DOMAIN',
        content: { type: 'string', value: (element: CommentSearchContent) => element.domain },
        sortable: true,
      },
      {
        dataFieldName: 'market',
        name: 'recipe.market',
        headerTranslationKey: 'COMMENTS.SEARCH.COMMENT_MARKET',
        content: { type: 'string', value: (element: CommentSearchContent) => element.market },
        sortable: true,
      },
      {
        dataFieldName: 'language',
        name: 'recipe.lang',
        headerTranslationKey: 'COMMENTS.SEARCH.COMMENT_LANG',
        content: { type: 'string', value: (element: CommentSearchContent) => element.language },
        sortable: true,
      },
      {
        dataFieldName: 'content',
        name: 'comment',
        headerTranslationKey: 'COMMENTS.SEARCH.COMMENT_CONTENT',
        content: { type: 'string', value: (element: CommentSearchContent) => element.content },
        sortable: false,
      },
      {
        dataFieldName: 'title',
        name: 'recipe.title',
        headerTranslationKey: 'COMMENTS.SEARCH.COMMENT_RECIPE',
        content: { type: 'string', value: (element: CommentSearchContent) => element.recipe.title },
        linkCallback: (element: CommentSearchContent) => {
          const functionalId = element.recipe.identifier.functionalId;
          const sourceSystem = element.recipe.identifier.sourceSystem;

          window.open(`recipes/edit/${sourceSystem}/${functionalId}`, '_blank');
        },
        sortable: true,
      },
      {
        dataFieldName: 'status',
        name: 'status',
        headerTranslationKey: 'COMMENTS.SEARCH.COMMENT_STATUS',
        content: { type: 'string', value: (element: CommentSearchContent) => element.status, isStatus: true },
        sortable: true,
      },
      {
        dataFieldName: 'creationDate',
        name: 'creationDate',
        headerTranslationKey: 'COMMENTS.SEARCH.COMMENT_CREATIONDATE',
        content: { type: 'date', format: 'dd/MM/yyyy' },
        sortable: true,
      },
      {
        dataFieldName: 'author',
        name: 'creator.nickname',
        headerTranslationKey: 'COMMENTS.SEARCH.COMMENT_AUTHOR',
        content: { type: 'string', value: (element: CommentSearchContent) => element.author },
        sortable: true,
        linkCallback: (element: CommentSearchContent) => {
          const functionalId = element.recipe.identifier.functionalId;
          const sourceSystem = element.recipe.identifier.sourceSystem;

          window.open(`#/users/edit/${sourceSystem}/${functionalId}`, '_blank');
        },
      },
    ];
  }
}
